/**
 * @fileoverview Canvas drawing component using Fabric.js.
 *
 * @author Louie Tian <louie.tian@logicsolutions.com>
 * @date August 14, 2024
 * @version 1.0.0
 *
 * This file includes functions controlling and displaying drawings on canvas using Fabric.js.
 */
import { DataService, AppService, LocalStorageService, HiddenDataService } from '../../../service';
import { Component, OnInit, AfterViewInit, Input, ChangeDetectorRef, OnChanges, SimpleChanges, Output, EventEmitter, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { fabric } from 'fabric';
import { PdfRendererService } from '../../../service/pdf-renderer.service';

import {
  ShapeType,
  ActionMode,
  DisplayActionMode,
  IconType,
  IconPath,
  FileType,
  ObjectRenderingProperty,
  LocationType,
  InteractiveMode,
  RiskLevel,

  HeatMapObjectOptions,
  HeatMapRectOptions,
  HeatMapCircleOptions,
  HeatMapPolygonOptions,
  HeatmapObject,
  HeatmapImage,
  HeatmapRect,
  HeatmapCircle,
  HeatmapPolygon,
  HeatmapPoint,

  getFillColor,
  getStrokeColor
} from './fabric.enum';
declare var Hammer: any;
import { TranslateService } from '@ngx-translate/core';
import _ from 'lodash';

@Component({
  selector: 'fabric-canvas-cn',
  templateUrl: './fabric-canvas-cn.component.html',
  styleUrls: ['./fabric-canvas-cn.component.scss']
})
export class FabricCanvasCnComponent implements OnInit, AfterViewInit, OnChanges {
  ShapeType: typeof ShapeType = ShapeType;
  ActionMode: typeof ActionMode = ActionMode;
  DisplayActionMode: typeof DisplayActionMode = DisplayActionMode;
  IconType: typeof IconType = IconType;
  InteractiveMode: typeof InteractiveMode = InteractiveMode;
  LocationType: typeof LocationType = LocationType;

  canvas!: fabric.Canvas;
  @ViewChild('fabricCanvas') fabricCanvasRef: ElementRef;

  @Input() width: number = 800;
  @Input() height: number = 600;

  @Input() interactiveMode: InteractiveMode = InteractiveMode.View;
  @Input() locationType: LocationType = LocationType.Point;
  @Input() fileType: FileType = FileType.JPG;

  @Input() floorPlanUrl: string = '';
  @Input() locationName: string = '';
  @Input() locationId: number;
  @Input() displayDifferentStates: boolean = false;
  @Input() shapes: any[] = []; // Array to store all shapes drawn on the canvas
  @Input() shouldShowTooltip: boolean = true; // Whether tooltip should be shown on click in View mode
  @Input() markPoint: boolean = false;
  @Input() viewAllPoint: boolean = false;
  @Input() locationData: any; // Data related to location this floor plan represents; used in risk heat map page
  @Input() onlyView: boolean = false; // Whether tooltip should be shown on click in View mode
  // @Input() selectedAreaText: any;
  @Input() pointData: any;
  @Input() closeClickZoom: boolean = false;
  @Input() iconPosition;
  @Output() selectedAreaEvent = new EventEmitter<any>();
  @Output() markPointEvent = new EventEmitter<any>();
  @Output() selectedPointEvent = new EventEmitter<any>();

  store = {
    scale: 1, //图片缩放
    pageX: '',
    pageY: '',
    // moveable:false,
    pageX2: '',
    pageY2: '',
    originScale: 0
  }

  top = 0 //y轴坐标
  left = 0 //x轴坐标
  ot = 0
  ol = 0
  private initShapes: any[] = []; // Array to store all shapes previously drawn on the canvas
  private currentDrawingMode: ShapeType | null = null;
  private actionMode: ActionMode = ActionMode.View;
  private displayActionMode: DisplayActionMode = DisplayActionMode.View;
  private iconType: IconType | null = null;
  private currentInstanceDrawn: boolean = false;
  private currentInstanceDrawnId: string = '';
  private canvasContainerElement: Element;
  isDrawing: boolean = false;
  points: fabric.Point[] = [];
  lines: fabric.Line[] = [];
  polygon!: fabric.Polygon;
  polygons: fabric.Polygon[] = []; // Array to store all temp polygons for current polygon drawing on the canvas
  pointRadius: number = 3;
  // Maintain a map to store active tooltips by shape ID
  activeTooltips: { [shapeId: string]: HTMLElement } = {};

  // 缩放控制变量
  zoomSpeed = 0.2; // 默认缩放速度
  minZoom = 0.1;   // 最小缩放比例
  maxZoom = 10;    // 最大缩放比例
  cickMaxZoom = 1.5
  isPanning = false; // 是否正在平移
  panStartPoint = null; // 平移起始点
  isSpacePressed = false; // 空格键是否按下
  markers = [];
  selectedArea;
  selectedPointText;
  panAnimationId;
  screenWidth;
  private hammerManager: any;
  private panViewportOrigin = { x: 0, y: 0 };
  private readonly panResistance = 0.35;

  constructor(
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    public appService: AppService,
    private pdfRendererService: PdfRendererService,
  ) { }

  ngOnInit(): void {
    this.setCanvasSizeInfo();
  }

  setCanvasSizeInfo() {
    const root = document.documentElement;
    // Inject the size info as CSS variables
    root.style.setProperty('--canvas-width', `${this.width}px`);
    root.style.setProperty('--canvas-max-width', `${this.width}px`);
    root.style.setProperty('--canvas-height', `${this.height}px`);
  }

  ngAfterViewInit(): void {
    // Initialize canvas
    this.canvas = new fabric.Canvas(this.fabricCanvasRef.nativeElement, {
      width: this.width,
      height: this.height,
      selection: true, // Enable selection for all objects by default
      defaultCursor: 'crosshair',
      moveCursor: 'move',
      // More accurate hit-testing for concave/self-intersecting shapes
      perPixelTargetFind: true,
      targetFindTolerance: 6,
      preserveObjectStacking: true,
      // centeredScaling: true
    });
    // console.log(this.width, this.height, '123123123')
    this.canvasContainerElement = this.canvas && this.canvas.wrapperEl ? this.canvas.wrapperEl : document.body;
    // Initialize canvas and data rendering
    this.initRender(true);

    // Set up event listeners for this instance
    this.setupEventListeners();
    // console.log(this.viewAllPoint, this.pointData, '123123123123')
    if (this.viewAllPoint) {
      // this.pointData.forEach((o) => {
      //   this.addMarkerAtPosition(Number(o.xAxis), Number(o.yAxis));
      //   console.log(o, '123123123');
      // })
    } else {

      if (this.pointData && this.pointData[0] && !this.iconPosition) {
        this.addMarkerAtPosition(Number(this.pointData[0].xAxis), Number(this.pointData[0].yAxis));
      }
    }
    // console.log(this.iconPosition,'this.iconPosition');
    if(this.iconPosition){
      this.addMarkerAtPosition(Number(this.iconPosition[0].xAxis), Number(this.iconPosition[0].yAxis));
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.setCanvasSizeInfo();
    // Initialize canvas and data rendering
    if (this.canvas) {
      this.initRender(true);
    }
  }

  initRender(incomingShapes: boolean) {
    if (incomingShapes) {
      // Initialize existing shapes
      this.initShapes = _.cloneDeep(this.shapes);
    }

    // Load background image onto canvas
    this.loadBackgroundImage(this.fileType, this.canvas);

    // Check if existing shapes already contain shape for this instance
    this.updateCurrentInstanceDrawnStatus(this.shapes);

    // Restore existing shapes onto canvas
    this.restoreDrawingOnCanvas(this.canvas, this.shapes);

    // Set interactive mode
    this.updateObjectControlStatus();


    // Remove all existing tooltips from canvas
    this.clearTooltips();   // 只画一个

    // 清除旧标记点，防止重复
    this.clearMarkers();

    if (this.viewAllPoint) {
      this.pointData.forEach((o) => {
        this.addMarkerAtPosition(Number(o.xAxis), Number(o.yAxis), o.id, o.status);
      })
    }
  }

  updateObjectControlStatus() {
    switch (this.interactiveMode) {
      case InteractiveMode.View:
        this.switchActionMode(ActionMode.View);
        break;

      case InteractiveMode.Edit:
        this.switchActionMode(ActionMode.Selection);
        break;
    }
  }

  updateCurrentInstanceDrawnStatus(shapes: Partial<HeatmapObject>[]): void {
    // Reset the flag for indicating whether drawing has happened on current instance
    this.currentInstanceDrawn = false;
    // Reset ID for newly drawn object on current instance
    this.currentInstanceDrawnId = '';
    // Loop through existing shapes and determine if there's a shape for current instance
    shapes.forEach(shape => {
      // If we find one that matches on location ID, then we treat it as the shape for current instance
      if (shape.locationId === this.locationId) {
        // Set flag to true since we found a match
        this.currentInstanceDrawn = true;
        // Save object ID for further reference
        this.currentInstanceDrawnId = shape.objectId;
      }
    });
  }

  loadBackgroundImage(fileType: FileType, canvas: fabric.Canvas) {
    const fileTypeStr = fileType ? fileType.replace('.', '') : null;
    if (this.floorPlanUrl) {
      switch (fileTypeStr) {
        case FileType.PDF:
          this.loadPDF(canvas);
          break;

        case FileType.JPG:
        case FileType.JPEG:
        case FileType.PNG:
        case FileType.SVG:
        default:
          this.loadImage(canvas);
      }
    }
  }

  loadPDF(canvas: fabric.Canvas) {
    this.pdfRendererService.loadPdfAsImage(this.floorPlanUrl).then(pdfCanvas => {
      const fabricImage = new fabric.Image(pdfCanvas, {
        left: 0,
        top: 0,
        selectable: false,  // Disable selection
        evented: false,     // Disable events (like drag)
        hasControls: false, // Disable controls (like rotating)
        sourcePath: this.floorPlanUrl // Store pdf url as a property
      } as HeatMapObjectOptions);

      fabricImage.set('scaleX', this.resizeImageToFitCanvas(fabricImage, canvas));
      fabricImage.set('scaleY', this.resizeImageToFitCanvas(fabricImage, canvas));
      //fabricImage.scaleToHeight(canvas.height);
      //fabricImage.scaleToWidth(canvas.width);

      // Add the image as a background image to the canvas
      canvas.setBackgroundImage(fabricImage, () => {
        canvas.renderAll();
      });
    });
  }

  loadImage(canvas: fabric.Canvas) {
    fabric.Image.fromURL(this.floorPlanUrl, (img) => {
      // console.log(img)
      img.set({
        left: 0,
        top: 0,
        scaleX: this.resizeImageToFitCanvas(img, canvas),
        scaleY: this.resizeImageToFitCanvas(img, canvas),
        selectable: false,  // Disable selection
        evented: false,     // Disable events (like drag)
        hasControls: false, // Disable controls (like rotating)
        sourcePath: this.floorPlanUrl // Store image url as a property
      } as HeatmapImage);

      //img.scaleToHeight(canvas.height);
      //img.scaleToWidth(canvas.width);

      // Add the image as a background image to the canvas
      canvas.setBackgroundImage(img, () => {
        canvas.renderAll();
      });
    });
  }

  // Load icon image onto canvas
  loadIcon(pointer: HeatmapPoint, iconType: IconType | null, canvas: fabric.Canvas) {
    if (!iconType) {
      console.warn('IconType is null or undefined.');
      return;
    }

    if (!canvas) {
      console.error('Canvas is not initialized.');
      return;
    }

    const iconUrl = IconPath[iconType];
    if (!iconUrl) {
      console.error(`No icon URL found for IconType: ${iconType}`);
      return;
    }

    // Set tooltip text for icon
    let tooltipText = `<span><b>${this.locationName}</b></span>`;

    // Generate unique ID for icon
    const uniqueId = this.generateUniqueId(ShapeType.Icon);

    // Load the icon and render it on canvas
    fabric.Image.fromURL(iconUrl, (img) => {
      img.set({
        left: pointer.x,
        top: pointer.y,
        scaleX: 0.05,
        scaleY: 0.05,
        originX: 'center', // Adjust originX to center
        originY: 'center',  // Adjust originY to center
        selectable: false,  // Make icon selectable
        evented: false,     // Disable events (like drag)
        hasControls: false, // Disable controls (like rotating)
        locationId: this.locationId,
        objectId: uniqueId,
        objectType: ShapeType.Icon,
        objectName: this.locationName,
        tooltipText: tooltipText,
        sourcePath: iconUrl // Store icon url as a property
      } as HeatmapImage);

      this.currentInstanceDrawnId = uniqueId; // Save unique ID for current instance item for ease of further handling
      this.currentInstanceDrawn = true; // Set flag for indicating whether drawing already happened on current instance to true
      canvas.add(img); // Add icon to canvas
      this.shapes.push(img); // Store reference to icon for future operations
      img.setCoords(); // Set coordinates for precise positioning
      img.bringToFront(); // Bring the icon to the front
      canvas.renderAll(); // Render canvas after adding the icon
      //this.setInteractiveMode(this.actionMode);
      // Set interactive mode
      this.updateObjectControlStatus();

      // Check if the icon is added to the canvas
      const objectsOnCanvas = canvas.getObjects();
      const isImageAdded = objectsOnCanvas.includes(img);

      if (isImageAdded) {
        console.log('Image successfully added to canvas:', img);
      } else {
        console.error('Failed to add icon to canvas.');
      }
    });
  }

  resizeImageToFitCanvas(img: fabric.Image, canvas: fabric.Canvas): number {
    // Get the dimensions of the canvas
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    // Get the dimensions of the image
    const imgWidth = img.width;
    const imgHeight = img.height;

    // Calculate the scale rate of both height and width
    const widthScale = canvasWidth / imgWidth;
    const heightScale = canvasHeight / imgHeight;

    // Define output scale that can fit the canvas
    let newScale: number = 0;

    // Calculate new scale for image to fit the canvas
    if (imgWidth > canvasWidth && imgHeight > canvasHeight) {
      // Image exceeds canvas limit on both width and height
      newScale = widthScale < heightScale ? widthScale : heightScale;
    } else if (imgWidth > canvasWidth) {
      // Image exceeds canvas only on width
      newScale = widthScale;
    } else if (imgHeight > canvasHeight) {
      // Image exceeds canvas only on height
      newScale = heightScale;
    } else {
      // Image does not exceed canvas
      newScale = widthScale < heightScale ? widthScale : heightScale;
    }

    return newScale;
  }

  setupEventListeners() {
    // Add event listeners
    this.canvas.on('mouse:down', (event: fabric.IEvent) => this.onMouseDown(event));
    this.canvas.on('mouse:move', (event: fabric.IEvent) => this.onMouseMove(event));
    this.canvas.on('mouse:up', (event: fabric.IEvent) => this.onMouseUp(event));

    // Listen for selection events
    this.canvas.on('selection:created', (event) => this.onSelectionCreated(event, this.canvas));
    this.canvas.on('selection:updated', (event) => this.onSelectionCreated(event, this.canvas));
    this.canvas.on('selection:cleared', () => this.onSelectionCleared(this.canvas));

    // Listen for object modified (including drag, rotate, scale) events
    this.canvas.on('object:modified', (event) => this.onObjectModified(event, this.canvas));

    // this.canvas.on('wheel', (event) => this.wheelFunction(event, this.canvas));
    // 滚轮
    // this.addWheelListener();
    this.addZoomn();
    this.setupKeyboardControls();
  }

  addZoomn() {
    // 缩放状态变量
    let initialZoom = 1;
    let lastScale = 1;
    let currentZoom = 1;
    let lastRotation = 0;
    let that = this;
    var hammertime = new Hammer(that.canvas.upperCanvasEl); // 使用 canvas 的上层画布元素
    
    // const pan = new Hammer.Pan({ direction: Hammer.DIRECTION_ALL });

    hammertime.get('pinch').set({ enable: true }); // 启用捏合（pinch）手势
    hammertime.get('pan').set({ enable: true, direction: Hammer.DIRECTION_ALL, threshold: 2 }); // 启用全方向平移
    // hammertime.get('rotate').set({ enable: true }); // 启用捏合（rotate）手势

    hammertime.on('pinchstart', function(ev) {
        // var scale = this.canvas.getZoom(); // 获取当前缩放级别
        // scale *= ev.scale; // 应用新的缩放比例
        // scale = Math.min(Math.max(.1, scale), 10); // 限制缩放范围，避免过小或过大
        // this.canvas.zoomToPoint({ x: ev.center.x, y: ev.center.y }, scale); // 缩放到指定点
    
        // 记录初始缩放值
        initialZoom = that.canvas.getZoom();
        lastScale = 1;
                
        // 禁用对象选择，避免干扰
        that.canvas.selection = false;
        that.canvas.forEachObject(obj => {
            obj.selectable = false;
        });
    });

    hammertime.on('pinchmove', function(e) {
        // 计算新的缩放值（降低手势灵敏度，避免轻微捏合导致大幅缩放）
        // e.scale 是相对于 pinchstart 时的缩放比例
        const pinchSensitivity = 0.35;
        const adjustedScale = 1 + ((e.scale - 1) * pinchSensitivity);
        let newZoom = initialZoom * adjustedScale;
        
        // 限制缩放范围（0.2 到 5 倍）
        newZoom = Math.max(0.2, Math.min(5, newZoom));
        
        // 获取触摸点中心位置
        const center = {
            x: e.center.x,
            y: e.center.y
        };
        
        // 将屏幕坐标转换为画布坐标
        const point = {
            x: (center.x - that.canvas.viewportTransform[4]) / that.canvas.getZoom(),
            y: (center.y - that.canvas.viewportTransform[5]) / that.canvas.getZoom()
        };
        
        // 应用缩放
        that.canvas.zoomToPoint(point, newZoom);
        
        // 更新状态
        currentZoom = newZoom;
        lastScale = e.scale;
        
        // // 更新显示
        // updateZoomInfo();
        // updateDebug('捏合移动', e);
    });


    // hammertime.on('rotate', function(ev) {
    //     var scale = this.canvas.getZoom(); // 获取当前缩放级别
    //     scale *= ev.scale; // 应用新的缩放比例
    //     scale = Math.min(Math.max(.1, scale), 10); // 限制缩放范围，避免过小或过大
    //     this.canvas.zoomToPoint({ x: ev.center.x, y: ev.center.y }, scale); // 缩放到指定点
    // });

    // 平移手势处理（当没有捏合时）
    let panLastDeltaX = 0;
    let panLastDeltaY = 0;
    let panVelocityX = 0;
    let panVelocityY = 0;
    let isPinching = false;

    hammertime.on('pinchstart', function(e) {
      isPinching = true;
    });

    hammertime.on('pinchend', function(e) {
      // 恢复对象选择
      isPinching = false;
      that.canvas.selection = true;
      // Restore selectability/eventing according to current mode
      that.updateObjectControlStatus();
      that.refreshAfterViewportChange();
    });

    hammertime.on('panstart', function(e) {
      if (isPinching) return;
      that.isPanning = true;
      panLastDeltaX = 0;
      panLastDeltaY = 0;
      panVelocityX = 0;
      panVelocityY = 0;
      if (that.panAnimationId) {
        cancelAnimationFrame(that.panAnimationId);
        that.panAnimationId = null;
      }
      that.canvas.selection = false;
      that.canvas.defaultCursor = 'grabbing';
    });

    hammertime.on('panmove', function(e) {
      if (isPinching || !that.isPanning) return;
      const dx = e.deltaX - panLastDeltaX;
      const dy = e.deltaY - panLastDeltaY;
      panVelocityX = dx;
      panVelocityY = dy;
      panLastDeltaX = e.deltaX;
      panLastDeltaY = e.deltaY;
      const vpt = that.canvas.viewportTransform;
      vpt[4] += dx;
      vpt[5] += dy;
      that.constrainPan();
      that.canvas.requestRenderAll();
    });

    hammertime.on('panend', function(e) {
      if (!that.isPanning) return;
      that.isPanning = false;
      that.canvas.selection = true;
      that.canvas.defaultCursor = 'default';
      that.applyPanMomentum(panVelocityX, panVelocityY);
      that.refreshAfterViewportChange();
    });
  }
  addWheelListener() {
    const canvasContainer = document.getElementById('canvasContainer');
    let that = this;
    // 鼠标滚轮事件
    // canvasContainer.addEventListener('wheel', function (e) {
    //   e.preventDefault();
    //   e.stopPropagation();

    //   const delta = e.deltaY;
    //   const pointer = that.canvas.getPointer(e);

    //   // 检查是否按下了Ctrl键 (Ctrl+滚轮快速缩放)
    //   const isCtrlPressed = e.ctrlKey;
    //   const speedMultiplier = isCtrlPressed ? 2 : 1;

    //   // 计算缩放方向和量
    //   let zoomDelta;
    //   if (delta < 0) {
    //     // 滚轮向上 - 放大
    //     zoomDelta = that.zoomSpeed * speedMultiplier;
    //   } else {
    //     // 滚轮向下 - 缩小
    //     zoomDelta = -that.zoomSpeed * speedMultiplier;
    //   }

    //   // 应用缩放
    //   that.zoomAtPoint(pointer, zoomDelta);

    //   // 显示缩放指示器
    //   that.showZoomIndicator(zoomDelta > 0 ? 'in' : 'out');
    // }, { passive: false });

    // // 触摸板捏合缩放 (可选)
    // let initialPinchDistance = null;
    // canvasContainer.addEventListener('touchstart', function(e) {
    //   if (e.touches.length === 2) {
    //       initialPinchDistance = that.getTouchDistance(e.touches[0], e.touches[1]);
    //   }
    // }, { passive: true });
    
    // canvasContainer.addEventListener('touchmove', function(e) {
    //   if (e.touches.length === 2 && initialPinchDistance !== null) {
    //     e.preventDefault();
    //     const currentDistance = that.getTouchDistance(e.touches[0], e.touches[1]);
    //     const scaleChange = currentDistance / initialPinchDistance;
        
    //     const touch1 = e.touches[0];
    //     const touch2 = e.touches[1];
    //     const centerX = (touch1.clientX + touch2.clientX) / 2;
    //     const centerY = (touch1.clientY + touch2.clientY) / 2;
        
    //     const pointer = {
    //         x: centerX - canvasContainer.getBoundingClientRect().left,
    //         y: centerY - canvasContainer.getBoundingClientRect().top
    //     };
        
    //     // 应用缩放
    //     const zoomChange = (scaleChange - 1) * 0.5;
    //     that.zoomAtPoint(pointer, zoomChange);
        
    //     initialPinchDistance = currentDistance;
    //   }
    // }, { passive: false });
    
    // canvasContainer.addEventListener('touchend', function() {
    //     initialPinchDistance = null;
    // }, { passive: true });

    // // 触摸屏手势缩放
    // canvasContainer.addEventListener('touch:gesture', function(options: any) {
    //     const scale = options.self.scale;
    //     const pointer = that.canvas.getPointer(options.e);
    //     let zoomLevel = Math.round(this.canvas.getZoom() * 100);

    //     zoomLevel *= scale;
    //     zoomLevel = Math.max(0.1, Math.min(5, zoomLevel));
        
    //     that.canvas.zoomToPoint(pointer, zoomLevel);
    //     that.updateZoomDisplay();
    // });
  }

  getTouchDistance(touch1, touch2) {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  zoomOnClick(event: fabric.IEvent) {
    if (!event || !event.e) return;

    const TARGET_ZOOM = 2.25; // 225%
    const pointer = this.canvas.getPointer(event.e);
    const currentZoom = this.canvas.getZoom();

    console.log(currentZoom, 'currentZoom');

    // 👉 已经 >= 225%，不再放大
    if (currentZoom >= TARGET_ZOOM) {
      return;
    }

    // 👉 直接跳到 225%
    this.canvas.zoomToPoint(pointer, TARGET_ZOOM);
    this.canvas.requestRenderAll();

    this.updateZoomDisplay();
  }

  /** 点击命中标记 Group 或其内部图片时，解析到带 name 的标记 Group */
  private resolveFloorPlanMarkerTarget(target: any): any {
    if (!target) {
      return null;
    }
    let t = target;
    for (let i = 0; i < 6 && t; i++) {
      if (t.isFloorPlanMarker) {
        return t;
      }
      t = t.group || t.parent;
    }
    return null;
  }

  onMouseDown(event: fabric.IEvent) {
    // this.zoomToArea(event.e);   // 只读的模式下，点击放大
    const pointer = this.canvas.getPointer(event.e); // Get mouse pointer coordinates
    // After viewportTransform pan/zoom, `event.target` can be null/unreliable.
    // Resolve target from DOM event for consistent hit-testing.
    const target: any = (event && event.e) ? (this.canvas.findTarget(event.e, true) as any) : (event as any).target;
    
    // 👉 点击自动放大225倍    -----点击后自动放大  先注释掉
    // if (!this.closeClickZoom) {
    //   this.zoomOnClick(event);
    // }
    //     // 点击事件
    // markerIcon.on('mousedown', function(e) {
    //   if (e.button === 1) { // 左键点击
    //       // showLocationDetails(this.data);
    //     const id = e.target.name;
    //     if (id && id !== '位置标记') {
    //       const item = that.pointData.find((l) => l.id == id);
    //       that.selectedPointEvent.emit(item);
    //     }

    //     // console.log(e)
    //   }
    // });
    const markerGroup = this.resolveFloorPlanMarkerTarget(target);
    const pointTarget =
      markerGroup || (target && target.name && target.name.indexOf('point') !== -1 ? target : null);
    if (pointTarget && pointTarget.name && pointTarget.name.indexOf('point') !== -1) {
      const id = pointTarget.name.split('_')[1];
      const item = this.pointData.find((l) => l.id == id);
      this.selectedPointEvent.emit(item);
      if (this.viewAllPoint) {
        this.selectedPointText = item;
      }
      // 点击平面图标记点：不再走区域选中，避免 selectedAreaEvent 误触发
      return;
    }
    console.log(target, 'target');
    if (target) {
      const selectedArea = this.shapes.find(shape => shape && target.objectId === shape.objectId);
      this.selectedArea = selectedArea;
      console.log(selectedArea, 'selectedArea');
      this.selectedAreaEvent.emit(selectedArea);
    } else {
      this.selectedArea = null;
      this.selectedAreaEvent.emit(null);
    }

    // 检查是否在平移模式下
    if (this.isSpacePressed) {
      this.isPanning = true;
      this.panStartPoint = {
        x: pointer.x,
        y: pointer.y,
        viewportTransform: this.canvas.viewportTransform.slice()
      };
      this.canvas.defaultCursor = 'grabbing';
      return;
    }
    // 检查是否在标记点模式下
    if (this.markPoint) {
      // 添加标记点
      if (this.markers && this.markers[0]) {
        if (this.markers[0].object) {
          this.canvas.remove(this.markers[0].object);
        }
        this.markers = [];
        // this.canvas.renderAll();
      }
      this.addMarkerAtPosition(pointer.x, pointer.y, this.selectedArea ? this.selectedArea.objectId : null);
      return;
    }

    if (this.currentDrawingMode === 'select') return;

    switch (this.actionMode) {
      // When action mode is Drawing, start drawing shape based on current drawing mode
      case ActionMode.Drawing:
        // Check if there's already a shape drawn for current instance
        if (this.currentInstanceDrawn && this.currentInstanceDrawnId) { // 只画一个
          // If there is, remove it from the canvas as well as shapes array
          this.canvas.remove((this.canvas.getObjects() as HeatmapObject[]).find(canvasObject => canvasObject.objectId === this.currentInstanceDrawnId));
          this.shapes.splice(this.shapes.findIndex(shape => shape && this.currentInstanceDrawnId === shape.objectId), 1);
        }
        switch (this.currentDrawingMode) {
          case ShapeType.Rectangle:
            this.startRectangle(pointer);
            break;
          case ShapeType.Circle:
            this.startCircle(pointer);
            break;
          case ShapeType.Polygon:
            this.addPoint(pointer);
            break;
          // When current drawing mode is icon, nothing needs to be handled here
          // since we place the icon on canvas when user releases mouse button
          case ShapeType.Icon:
            break;
        }
        break;

      // When action mode is Selection, nothing needs to be handled specifically
      case ActionMode.Selection:
        break;

      // When action mode is View, nothing needs to be handled specifically
      // since we show the tooltip when user releases mouse button
      case ActionMode.View:
        break;
    }
  }

  onMouseMove(event: fabric.IEvent) {
    const pointer = this.canvas.getPointer(event.e); // Get mouse pointer coordinates
    // 处理平移
    // if (this.isPanning && this.panStartPoint) {
    //   const dx = pointer.x - this.panStartPoint.x;
    //   const dy = pointer.y - this.panStartPoint.y;

    //   this.canvas.setViewportTransform([
    //     this.panStartPoint.viewportTransform[0],
    //     this.panStartPoint.viewportTransform[1],
    //     this.panStartPoint.viewportTransform[2],
    //     this.panStartPoint.viewportTransform[3],
    //     this.panStartPoint.viewportTransform[4] + dx,
    //     this.panStartPoint.viewportTransform[5] + dy
    //   ]);

    //   this.canvas.requestRenderAll();
    //   return;
    // }

    if (!this.isDrawing) return;

    switch (this.actionMode) {
      // When action mode is Drawing, start resizing the shape based on current drawing mode
      case ActionMode.Drawing:
        switch (this.currentDrawingMode) {
          case ShapeType.Rectangle:
            this.resizeRectangle(pointer);
            break;
          case ShapeType.Circle:
            this.resizeCircle(pointer);
            break;
          // When drawing a polygon, nothing needs to be handled here
          // since polygon drawing only involves clicking and no dragging is needed
          case ShapeType.Polygon:
            break;
          // When current drawing mode is icon, nothing needs to be handled here
          // since we place the icon on canvas when user releases mouse button
          case ShapeType.Icon:
            break;
        }
        break;

      // When action mode is Selection, nothing needs to be handled specifically
      case ActionMode.Selection:
        break;

      // When action mode is View, nothing needs to be handled specifically
      // since we show the tooltip when user releases mouse button
      case ActionMode.View:
        break;
    }
  }

  onMouseUp(event: fabric.IEvent) {
    const pointer = this.canvas.getPointer(event.e); // Get mouse pointer coordinates

    if (this.isPanning) {
      this.isPanning = false;
      this.panStartPoint = null;
      this.updateCursorForPanning(this.isSpacePressed);
      return;
    }

    switch (this.actionMode) {
      // When action mode is Drawing, finish the drawing based on current drawing mode
      case ActionMode.Drawing:
        switch (this.currentDrawingMode) {
          case ShapeType.Rectangle:
            this.finishRectangle();
            break;
          case ShapeType.Circle:
            this.finishCircle();
            break;
          case ShapeType.Polygon:
            this.updatePolygon();
            break;
          case ShapeType.Icon:
            this.loadIcon(pointer, this.getIconType(), this.canvas);
            break;
        }
        break;

      // When action mode is Selection, nothing needs to be handled specifically
      case ActionMode.Selection:
        break;

      // When action mode is View, we show the tooltip for the shape when user releases a click from it
      case ActionMode.View:
        // Check if the mouse pointer intersects with any shape
        const target = this.canvas.findTarget(event.e, true) as any;
        if (target && target !== this.canvas) {
          // Obtain tooltip from the shape
          const tooltipText = target.tooltipText ? target.tooltipText : null;
          // Only show tooltip when allowed
          if (this.shouldShowTooltip && tooltipText && !this.markPoint) {
            this.showTooltip(event, tooltipText, this.canvas, target);
          }
        }
        break;
    }
  }

  onSelectionCreated(event: any, canvas: fabric.Canvas) {
    // Enable delete action for selected object
    const selectedObject = event.selected[0] as fabric.Object;
    selectedObject.hasControls = true;
    selectedObject.set('hasControls', true);
    selectedObject.set('cornerColor', 'red'); // Optionally change corner color for indication
    canvas.renderAll();
  }

  onSelectionCleared(canvas: fabric.Canvas) {
    // Disable delete action when nothing is selected
    canvas.getObjects().forEach(object => {
      object.set('hasControls', false);
      object.set('cornerColor', 'rgba(0,0,0,0)'); // Set corner color to transparent
    });
    canvas.renderAll();
  }

  onObjectModified(event: any, canvas: fabric.Canvas) {
    const modifiedObject = event.target as HeatmapObject;

    // Update the shapes array with modified properties
    const index = this.shapes.findIndex(shape => shape && modifiedObject.objectId === shape.objectId);
    if (index !== -1) {
      modifiedObject.setCoords();
      this.shapes[index] = modifiedObject.toObject([
        // General properties that apply to all shapes
        ...ObjectRenderingProperty.General,
        // Rectangle specifc properties
        ...ObjectRenderingProperty[ShapeType.Rectangle],
        // Circle specific properties
        ...ObjectRenderingProperty[ShapeType.Circle],
        // Polygon specific properties
        ...ObjectRenderingProperty[ShapeType.Polygon],
        // Icon specific properties
        ...ObjectRenderingProperty[ShapeType.Icon],
      ]);
    }

    canvas.renderAll();
  }

  removeSelectedObjectFromCanvasAndStorage(canvas: fabric.Canvas, shapes: any[]) {
    const activeObject = canvas.getActiveObject() as HeatmapObject;
    // Ignore delete if there's no selected object
    if (!activeObject) {
      // Show an error message to user and terminate the action
      this.appService.toastTip('shared_components.fabric_canvas.please_select_first', true);
      return;
    }
    // Ignore delete if the selected object is not the one newly created this time
    if (activeObject.objectId !== this.currentInstanceDrawnId) {
      // Show an error message to user and terminate the action
      this.appService.toastTip('shared_components.fabric_canvas.delete_current_instance_only', true);
      return;
    }
    // Remove from canvas
    canvas.remove(activeObject);
    // Deselect the removed object
    canvas.discardActiveObject();
    // Remove from shapes array in place
    shapes.splice(shapes.findIndex(shape => shape && activeObject.objectId === shape.objectId), 1);
    // Render all remaining objects on canvas
    canvas.renderAll();
    // Reset the flag for indicating whether drawing has happened on current instance
    this.currentInstanceDrawn = false;
    // Reset ID for newly drawn object on current instance
    this.currentInstanceDrawnId = '';
  }

  setInteractiveMode(actionMode: ActionMode) {
    switch (actionMode) {
      // When in drawing mode, disable interactions for all objects on canvas
      case ActionMode.Drawing:
        this.canvas.getObjects().forEach(object => {
          object.set({
            selectable: false,
            evented: false, // Disable event firing (like selection)
            lockMovementX: true, // Disable horizontal movement
            lockMovementY: true, // Disable vertical movement
            lockRotation: true, // Disable rotation
            lockScalingX: true, // Disable scaling along X axis
            lockScalingY: true, // Disable scaling along Y axis
            lockScalingFlip: true, // Disable scale flipping
            lockSkewingX: true, // Disable skewing along X axis
            lockSkewingY: true // Disable skewing along Y axis
          });
        });
        break;

      // When in selection mode, allow selection and other interactions based on type of the object
      case ActionMode.Selection:
        this.canvas.getObjects().forEach((object: HeatmapObject) => {
          // Interactions should only be allowed on object belonging to current instance
          if (object.locationId === this.locationId) {
            switch (object.objectType) {
              // For icons, we only allow movement and disable all other actions
              // such as rotating or scaling
              case ShapeType.Icon:
                object.set({
                  selectable: true,
                  evented: true, // Ensure events are still fired (like selection)
                  lockMovementX: false, // Enable horizontal movement
                  lockMovementY: false, // Enable vertical movement
                  lockRotation: true, // Disable rotation
                  lockScalingX: true, // Disable scaling along X axis
                  lockScalingY: true, // Disable scaling along Y axis
                  lockScalingFlip: true, // Disable scale flipping
                  lockSkewingX: true, // Disable skewing along X axis
                  lockSkewingY: true // Disable skewing along Y axis
                });
                break;

              // For other types, we can enable all actions
              case ShapeType.Rectangle:
              case ShapeType.Circle:
              case ShapeType.Polygon:
              default:
                object.set({
                  selectable: true,
                  evented: true, // Ensure events are still fired (like selection)
                  lockMovementX: false, // Enable horizontal movement
                  lockMovementY: false, // Enable vertical movement
                  lockRotation: false, // Enable rotation
                  lockScalingX: false, // Enable scaling along X axis
                  lockScalingY: false, // Enable scaling along Y axis
                  lockScalingFlip: false, // Enable scale flipping
                  lockSkewingX: false, // Enable skewing along X axis
                  lockSkewingY: false // Enable skewing along Y axis
                });
                break;
            }
          } else {
            // Otherwise, enable selection but disable all interactions
            object.set({
              selectable: true,
              evented: true, // Ensure events are still fired (like selection)
              lockMovementX: true, // Disable horizontal movement
              lockMovementY: true, // Disable vertical movement
              lockRotation: true, // Disable rotation
              lockScalingX: true, // Disable scaling along X axis
              lockScalingY: true, // Disable scaling along Y axis
              lockScalingFlip: true, // Disable scale flipping
              lockSkewingX: true, // Disable skewing along X axis
              lockSkewingY: true // Disable skewing along Y axis
            });
          }
          object.setCoords();
        });
        break;

      // When in view mode, still enable selection but disable all other interactions on all objects
      case ActionMode.View:
        this.canvas.getObjects().forEach(object => {
          object.set({
            selectable: false,
            evented: true, // Ensure events are still fired (like selection)
            lockMovementX: true, // Disable horizontal movement
            lockMovementY: true, // Disable vertical movement
            lockRotation: true, // Disable rotation
            lockScalingX: true, // Disable scaling along X axis
            lockScalingY: true, // Disable scaling along Y axis
            lockScalingFlip: true, // Disable scale flipping
            lockSkewingX: true, // Disable skewing along X axis
            lockSkewingY: true // Disable skewing along Y axis
          });
          object.setCoords();
        });
        break;
    }
    this.canvas.renderAll();
  }

  generateUniqueId(shapeType: ShapeType): string {
    // Helper function to generate unique IDs
    return shapeType + '_' + Math.random().toString(36).substring(2, 11);
  }

  startRectangle(pointer: HeatmapPoint) {
    this.isDrawing = true;
    const uniqueId = this.generateUniqueId(ShapeType.Rectangle);
    const rect = new fabric.Rect({
      left: pointer.x,
      top: pointer.y,
      width: 0,
      height: 0,
      fill: getFillColor(RiskLevel.NA),
      stroke: getStrokeColor(RiskLevel.NA),
      strokeWidth: 2,
      scaleX: 1,
      scaleY: 1,
      angle: 0,
      selectable: false,
      locationId: this.locationId,
      objectId: uniqueId,
      objectType: ShapeType.Rectangle,
      objectName: this.locationName,
      tooltipText: `<span><b>${this.locationName}</b></span>`
    } as HeatMapRectOptions);
    this.currentInstanceDrawnId = uniqueId; // Save unique ID for current instance item for ease of further handling
    this.canvas.add(rect);
    this.shapes.push(rect);
    // Set interactive mode to Drawing when drawing starts to prevent drawing and selection happen at the same time
    this.setInteractiveMode(ActionMode.Drawing);
    this.canvas.renderAll();
  }

  resizeRectangle(pointer: HeatmapPoint) {
    if (!this.isDrawing) return;

    const rect = this.shapes[this.shapes.length - 1] as fabric.Rect;
    rect.set({ width: pointer.x - rect.left, height: pointer.y - rect.top });
    this.canvas.renderAll();
  }

  finishRectangle() {
    this.isDrawing = false;
    // Set interactive mode to View when drawing is complete
    this.setInteractiveMode(ActionMode.View);
    // Set flag for indicating whether drawing already happened on current instance to true
    this.currentInstanceDrawn = true;
  }

  startCircle(pointer: HeatmapPoint) {
    this.isDrawing = true;
    const uniqueId = this.generateUniqueId(ShapeType.Circle);
    const circle = new fabric.Circle({
      left: pointer.x,
      top: pointer.y,
      radius: 0,
      originX: 'center', // Adjust originX to center
      originY: 'center',  // Adjust originY to center
      fill: getFillColor(RiskLevel.NA),
      stroke: getStrokeColor(RiskLevel.NA),
      strokeWidth: 2,
      scaleX: 1,
      scaleY: 1,
      angle: 0,
      selectable: false,
      locationId: this.locationId,
      objectId: uniqueId,
      objectType: ShapeType.Circle,
      objectName: this.locationName,
      tooltipText: `<span><b>${this.locationName}</b></span>`
    } as HeatMapCircleOptions);
    this.currentInstanceDrawnId = uniqueId; // Save unique ID for current instance item for ease of further handling
    this.canvas.add(circle);
    this.shapes.push(circle);
    circle.setCoords();
    // Set interactive mode to Drawing when drawing starts to prevent drawing and selection happen at the same time
    this.setInteractiveMode(ActionMode.Drawing);
  }

  resizeCircle(pointer: HeatmapPoint) {
    if (!this.isDrawing) return;

    const circle = this.shapes[this.shapes.length - 1] as fabric.Circle;
    const radius = Math.abs(pointer.x - circle.left);
    circle.set({ radius: radius });
    circle.setCoords();
    this.canvas.renderAll();
  }

  finishCircle() {
    this.isDrawing = false;
    // Set interactive mode to View when drawing is complete
    this.setInteractiveMode(ActionMode.View);
    // Set flag for indicating whether drawing already happened on current instance to true
    this.currentInstanceDrawn = true;
  }

  addPoint(pointer: HeatmapPoint) {
    // Mark a point
    const circle = new fabric.Circle({
      left: pointer.x - this.pointRadius,
      top: pointer.y - this.pointRadius,
      radius: this.pointRadius,
      fill: 'black',
      selectable: false,
      shouldBeRemoved: true
    } as HeatMapCircleOptions);
    this.canvas.add(circle);
    this.shapes.push(circle);

    // Add point to the polygon points array
    this.points.push(new fabric.Point(pointer.x, pointer.y));

    // Update the polygon shape
    this.updatePolygon();
    this.setInteractiveMode(ActionMode.Drawing);
  }

  updatePolygon() {
    // Clear previous polygon
    if (this.polygons.includes(this.polygon)) {
      this.canvas.remove(this.polygon);
    }
    const uniqueId = this.generateUniqueId(ShapeType.Polygon);
    // Set flag for indicating whether drawing already happened on current instance to true
    this.currentInstanceDrawn = true;
    // Create new polygon with updated points
    const points: fabric.Point[] = this.points.map(point => (new fabric.Point(point.x, point.y)));
    this.polygon = new fabric.Polygon(points, {
      fill: getFillColor(RiskLevel.NA),
      stroke: getStrokeColor(RiskLevel.NA),
      strokeWidth: 2,
      scaleX: 1,
      scaleY: 1,
      angle: 0,
      selectable: true,
      perPixelTargetFind: true,
      objectCaching: false,
      locationId: this.locationId,
      objectId: uniqueId,
      objectType: ShapeType.Polygon,
      objectName: this.locationName,
      tooltipText: `<span><b>${this.locationName}</b></span>`
    } as HeatMapPolygonOptions);
    // Save unique ID for current instance item for ease of further handling
    this.currentInstanceDrawnId = uniqueId;
    // Update the current polygon on canvas
    this.canvas.add(this.polygon);
    // Add this iteration of the polygon into temp polygons array
    this.polygons.push(this.polygon);
    // Render all objects on canvas
    this.canvas.renderAll();
    // Check if polygon is closed
    if (this.isPolygonClosed()) {
      this.finishPolygon();
    }
  }

  isPolygonClosed(): boolean {
    // Check if the polygon is closed by comparing the first and last points
    return this.points.length > 2 &&
      this.points[0].x === this.points[this.points.length - 1].x &&
      this.points[0].y === this.points[this.points.length - 1].y;
  }

  finishPolygon() {
    // Finish the polygon drawing process
    this.points = []; // Clear points array for next polygon
    // Set interactive mode to View when drawing is complete
    this.setInteractiveMode(ActionMode.View);
  }

  cleanupNonSelectableShapes() {
    // Remove non-selectable shapes (like polygon points) from canvas
    const canvasObject = this.canvas.getObjects() as HeatmapObject[];
    for (let i = canvasObject.length - 1; i >= 0; i--) {
      if (canvasObject[i] && canvasObject[i].shouldBeRemoved) {
        this.canvas.remove(canvasObject[i]);
      }
    }
    // Remove non-selectable shapes (like polygon points) from shapes array
    this.shapes = this.shapes.filter(shape => shape && !shape.shouldBeRemoved);
    // Check if a polygon has completed drawing
    if (this.polygons.length) {
      // When the newly drawn polygon is fully completed, add it into shapes array
      this.shapes.push(this.polygons[this.polygons.length - 1]);
    }
    // Clear the temp polygons array
    this.polygons = [];
  }

  // Method for setting current drawing mode with post processing
  setDrawingMode(mode: ShapeType | null) {
    // Check for mode change to prevent multiple consecutive click of the same button clearing existing shapes
    if (this.currentDrawingMode !== mode) {
      this.currentDrawingMode = mode;
      this.isDrawing = false;
      this.points = []; // Clear points array when switching drawing modes
      this.cleanupNonSelectableShapes(); // Clean up non-selectable shapes
    }
  }

  // Method for getting current drawing mode
  getDrawingMode() {
    return this.currentDrawingMode;
  }

  // Method for setting action mode only
  setActionMode(actionMode: ActionMode) {
    this.actionMode = actionMode;
  }

  // Method for getting current action mode
  getActionMode() {
    return this.actionMode;
  }

  // Method for setting display action mode only
  setDisplayActionMode(displayActionMode: DisplayActionMode) {
    this.displayActionMode = displayActionMode;
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  // Method for getting current display action mode
  getDisplayActionMode() {
    return this.displayActionMode;
  }

  // Method for setting icon type only
  setIconType(iconType: IconType | null) {
    this.iconType = iconType;
  }

  // Method for getting current icon type
  getIconType() {
    return this.iconType;
  }

  // Method for setting action mode and adjusting drawing mode accordingly
  switchActionMode(actionMode: ActionMode, drawingMode?: ShapeType | null, iconType?: IconType | null) {
    this.setActionMode(actionMode);
    this.setInteractiveMode(actionMode);
    switch (actionMode) {
      case ActionMode.Selection:
        this.setDrawingMode(null);
        this.setDisplayActionMode(DisplayActionMode.Selection);
        break;
      case ActionMode.View:
        this.setDrawingMode(null);
        this.setDisplayActionMode(DisplayActionMode.View);
        break;

      case ActionMode.Drawing:
        this.setDrawingMode(drawingMode ? drawingMode : this.getDrawingMode());
        this.setIconType(iconType ? iconType : this.getIconType());
        switch (drawingMode) {
          case ShapeType.Rectangle:
            this.setDisplayActionMode(DisplayActionMode.RectDrawing);
            break;

          case ShapeType.Circle:
            this.setDisplayActionMode(DisplayActionMode.CircleDrawing);
            break;

          case ShapeType.Polygon:
            this.setDisplayActionMode(DisplayActionMode.PolygonDrawing);
            break;

          case ShapeType.Icon:
            this.setDisplayActionMode(DisplayActionMode.IconDrawing);
            break;
        }
        break;
    }
  }

  // Method to serialize drawing data
  serializeDrawingData(shapes: Partial<HeatmapObject>[]): Partial<HeatmapObject>[] {
    const serializedData: Partial<HeatmapObject>[] = [];

    shapes.forEach(shape => {
      if (shape) {
        let serializedShape: Partial<HeatmapObject> = {
          aCoords: shape.aCoords,
          oCoords: shape.oCoords,
          left: shape.left,
          top: shape.top,
          stroke: shape.stroke,
          strokeWidth: shape.strokeWidth,
          fill: shape.fill,
          scaleX: shape.scaleX,
          scaleY: shape.scaleY,
          angle: shape.angle,
          locationId: shape.locationId,
          objectId: shape.objectId,
          objectType: shape.objectType,
          objectName: shape.objectName,
          tooltipText: shape.tooltipText,
          type: shape.type,
          length: shape.length,
          riskLevel: shape.riskLevel,
          iconType: shape.iconType,
          titleText: shape.titleText,
          contractorWorkCount: shape.contractorWorkCount,
          highRiskCount: shape.highRiskCount,
          personnelCount: shape.personnelCount,
          subIcons: shape.subIcons,
          // Add more properties applicable to all types of shape as needed
        };

        switch (shape.objectType) {
          case ShapeType.Rectangle:
            const rect = shape as HeatmapRect;
            serializedShape = {
              ...serializedShape,
              width: rect.width,
              height: rect.height,
              // Add more properties specific to rectangles
            };
            break;

          case ShapeType.Circle:
            const circle = shape as HeatmapCircle;
            serializedShape = {
              ...serializedShape,
              radius: circle.radius,
              originX: circle.originX,
              originY: circle.originY,
              // Add more properties specific to circles
            };
            break;

          case ShapeType.Polygon:
            const polygon = shape as HeatmapPolygon;
            serializedShape = {
              ...serializedShape,
              points: polygon.points,
              // Add more properties specific to polygons
            };
            break;

          case ShapeType.Icon:
            const icon = shape as HeatmapImage;
            serializedShape = {
              ...serializedShape,
              originX: icon.originX,
              originY: icon.originY,
              scaleX: icon.scaleX,
              scaleY: icon.scaleY,
              sourcePath: icon.sourcePath,
              // Add more properties specific to images
            };
            break;
        }
        serializedData.push(serializedShape);
      }
    });
    return serializedData;
  }

  restoreDrawingOnCanvas(canvas: fabric.Canvas, shapes: Partial<HeatmapObject>[]) {
    // Clear existing canvas objects
    canvas.clear();

    // Iterate through each serialized shape data 
    shapes.forEach(shapeData => {
      // console.log(shapeData, '1111111');
      let fabricObject: fabric.Object | null = null;
      const basicShapeData: Partial<HeatmapObject> = {
        aCoords: shapeData.aCoords,
        oCoords: shapeData.oCoords,
        left: shapeData.left,
        top: shapeData.top,
        stroke: shapeData.stroke,
        strokeWidth: shapeData.strokeWidth,
        fill: shapeData.fill,
        scaleX: shapeData.scaleX,
        scaleY: shapeData.scaleY,
        angle: shapeData.angle,
        locationId: shapeData.locationId,
        objectId: shapeData.objectId,
        objectType: shapeData.objectType,
        objectName: shapeData.objectName,
        tooltipText: shapeData.tooltipText,
        type: shapeData.type,
        length: shapeData.length,
        riskLevel: shapeData.riskLevel,
        iconType: shapeData.iconType,
        titleText: shapeData.titleText,
        contractorWorkCount: shapeData.contractorWorkCount,
        highRiskCount: shapeData.highRiskCount,
        personnelCount: shapeData.personnelCount,
        subIcons: shapeData.subIcons,
        // Add more properties applicable to all types of shape as needed
      };

      // Create fabric object based on object type
      switch (shapeData.objectType) {
        case ShapeType.Rectangle:
          fabricObject = new fabric.Rect({
            ...basicShapeData,
            width: shapeData.width,
            height: shapeData.height,
            // Add other properties specific to rectangles as needed
          });
          break;

        case ShapeType.Circle:
          if (shapeData.radius) {
            // Ensure radius is positive
            const radius = Math.abs(shapeData.radius);

            fabricObject = new fabric.Circle({
              ...basicShapeData,
              radius: radius,
              originX: shapeData.originX,
              originY: shapeData.originY,
              // Add other properties specific to circles as needed
            });
          }
          break;

        case ShapeType.Polygon:
          fabricObject = new fabric.Polygon(shapeData.points, {
            ...basicShapeData,
            perPixelTargetFind: true,
            objectCaching: false,
            // Add other properties specific to polygons as needed
          });
          break;

        case ShapeType.Icon:
          if (shapeData.sourcePath) {
            fabric.Image.fromURL(shapeData.sourcePath, (img) => {
              img.set({
                ...basicShapeData,
                scaleX: shapeData.scaleX,
                scaleY: shapeData.scaleY,
                originX: shapeData.originX,
                originY: shapeData.originY,
                sourcePath: shapeData.sourcePath,
                // Add other properties specific to icon as needed
              } as HeatmapImage);
              canvas.add(img); // Add the icon to canvas inside the callback
              img.setCoords(); // Set coordinates for precise positioning
              img.bringToFront(); // Bring the icon to the front
              canvas.renderAll(); // Render all objects on the canvas
              // Set interactive mode
              this.updateObjectControlStatus();
            });
          }
          break;
        // Add more cases as needed for other types of shapes
      }

      // Add the fabric object to the new canvas outside of the fabric.Image.fromURL callback
      if (fabricObject) {
        canvas.add(fabricObject);
        fabricObject.setCoords(); // Update object coordinates
      }
      // const name = shapeData.tooltipText.replace('<span><b>', '').replace('</b></span>', '');
      // console.log(shapeData, '435345345345')
      // if (name && name !== 'undefined') {
      //   // const offsetX = (shapeData.width/3).toFixed(2);
      //   const test = new fabric.Text(name, {
      //     left: shapeData.left + 20,
      //     top: shapeData.top + 20,
      //     fontSize: 16,
      //     fill: '#333',
      //   });

      //   canvas.add(test);
      // }
    });

    canvas.renderAll(); // Render all objects on the canvas
  }

  clearCanvas(canvas: fabric.Canvas) {
    // Clear existing canvas objects
    canvas.clear();
    // Copy objects drawn in previous instances and overwrite existing shapes array
    this.shapes = _.cloneDeep(this.initShapes);
    // If drawing mode is polygon prior on resetting, clear all temp polygons from array
    if (this.currentDrawingMode === ShapeType.Polygon) {
      this.polygons = [];
    }
    // Initialize canvas and data rendering
    this.initRender(false);
  }

  saveDrawnShapes() {
    // Save processed shape data into shapes array
    this.shapes = this.serializeDrawingData(this.shapes);
  }

  // Method to show tooltip at mouse position
  showTooltip(event: any, text: string, canvas: fabric.Canvas, shape: HeatmapObject) {
    const shapeId = shape.objectId; // Unique identifier of the current shape

    // Check if a tooltip is already active for this shape
    if (this.activeTooltips[shapeId]) {
      // If active tooltip exists, remove it
      this.canvasContainerElement.removeChild(this.activeTooltips[shapeId]);
      delete this.activeTooltips[shapeId];
    }

    // Calculate mouse position relative to canvas
    //const canvasElement = canvas.getElement();
    const pointer = canvas.getPointer(event.e);
    //const mouseRelativeX = pointer.x - canvasElement.getBoundingClientRect().left;
    //const mouseRelativeY = pointer.y - canvasElement.getBoundingClientRect().top;
    const mouseRelativeX = pointer.x;
    const mouseRelativeY = pointer.y;

    // Create the tooltip element if it doesn't exist
    let tooltip = document.createElement('div');
    tooltip.innerHTML = text;
    tooltip.style.position = 'absolute';
    tooltip.style.left = mouseRelativeX + 'px';
    tooltip.style.top = mouseRelativeY + 'px';
    tooltip.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
    tooltip.style.color = 'white';
    tooltip.style.padding = '15px';
    tooltip.style.borderRadius = '5px';

    // Add close button to tooltip
    const closeButton = document.createElement('span');
    closeButton.innerHTML = '×';
    closeButton.style.position = 'absolute';
    closeButton.style.top = '5px';
    closeButton.style.right = '5px';
    closeButton.style.cursor = 'pointer';
    tooltip.appendChild(closeButton);

    // Append tooltip to the document body
    this.canvasContainerElement.appendChild(tooltip);

    // Store the tooltip reference in activeTooltips
    this.activeTooltips[shapeId] = tooltip;

    // Add event listener to close button
    closeButton.addEventListener('click', () => {
      // Remove tooltip from DOM
      this.canvasContainerElement.removeChild(tooltip);
      delete this.activeTooltips[shapeId];
    });
  }

  // addTooltips(event: any, text: string, canvas: fabric.Canvas, shape: HeatmapObject) {
  //   // this.isDrawing = true;
  //   // const uniqueId = this.generateUniqueId(ShapeType.Rectangle);
  //   // const rect = new fabric.Rect({
  //   //   left: pointer.x,
  //   //   top: pointer.y,
  //   //   width: 0,
  //   //   height: 0,
  //   //   fill: getFillColor(RiskLevel.NA),
  //   //   stroke: getStrokeColor(RiskLevel.NA),
  //   //   strokeWidth: 2,
  //   //   scaleX: 1,
  //   //   scaleY: 1,
  //   //   angle: 0,
  //   //   selectable: false,
  //   //   locationId: this.locationId,
  //   //   objectId: uniqueId,
  //   //   objectType: ShapeType.Rectangle,
  //   //   objectName: this.locationName,
  //   //   tooltipText: `<span><b>${this.locationName}</b></span>`
  //   // } as HeatMapRectOptions);
  //   console.log(shape, '999999999');
  //   const test = new fabric.Text('办公区', {
  //       left: 850,
  //       top: 180,
  //       fontSize: 20,
  //       fill: '#40c057',
  //   });

  //   // this.currentInstanceDrawnId = uniqueId; // Save unique ID for current instance item for ease of further handling
  //   this.canvas.add(test);
  //   // this.shapes.push(rect);
  //   // // Set interactive mode to Drawing when drawing starts to prevent drawing and selection happen at the same time
  //   // this.setInteractiveMode(ActionMode.Drawing);
  //   this.canvas.renderAll();
  // }

  clearTooltips() {
    // Iterate through all active tooltips
    for (const shapeId in this.activeTooltips) {
      if (this.activeTooltips.hasOwnProperty(shapeId)) {
        // Remove each tooltip from the canvas container
        this.canvasContainerElement.removeChild(this.activeTooltips[shapeId]);
        // Delete the tooltip from the activeTooltips object
        delete this.activeTooltips[shapeId];
      }
    }
  }

  clearMarkers() {
    // 从画布上移除所有标记点对象
    this.markers.forEach(m => {
      if (m.object) {
        this.canvas.remove(m.object);
      }
    });
    this.markers = [];
  }

  zoomToArea(area) {
    if (!area) return;
    this.canvas.zoomToPoint({ x: area.clientX, y: area.clientY }, 1.2);
  }

  // 在指定点缩放
  zoomAtPoint(point, zoomDelta) {
    const currentZoom = this.canvas.getZoom();
    let newZoom = currentZoom * (1 + zoomDelta);

    // 应用缩放限制
    newZoom = Math.max(this.minZoom, Math.min(this.maxZoom, newZoom));

    if (newZoom === currentZoom) {
      // 缩放已达到限制
      this.showZoomIndicator('limit');
      return;
    }

    // 计算缩放点
    this.canvas.zoomToPoint(point, newZoom);

    // 更新缩放显示
    this.updateZoomDisplay();

    // 计算缩放百分比变化
    const zoomPercentChange = Math.round((newZoom - currentZoom) / currentZoom * 100);

    // 显示缩放指示器
    if (zoomDelta > 0) {
      this.showZoomIndicator('in', zoomPercentChange);
    } else {
      this.showZoomIndicator('out', Math.abs(zoomPercentChange));
    }

    // 更新状态栏
    this.updateStatus(`缩放: ${Math.round(newZoom * 100)}% | 使用鼠标滚轮继续缩放`);
  }

  // 更新状态
  updateStatus(message) {
    document.getElementById('statusBar').textContent = message;
  }

  // 更新缩放显示
  updateZoomDisplay() {
    const zoomPercent = Math.round(this.canvas.getZoom() * 100);
    document.getElementById('zoomLevel').textContent = `缩放: ${zoomPercent}%`;
  }

  // 显示缩放指示器
  showZoomIndicator(type, percent = 0) {
    const indicator = document.getElementById('zoomIndicator');
    const icon = document.getElementById('zoomIndicatorIcon');
    const text = document.getElementById('zoomIndicatorText');

    if (type === 'in') {
      icon.className = 'fas fa-search-plus';
      text.textContent = `+${percent}%`;
      indicator.style.color = '#10b981';
    } else if (type === 'out') {
      icon.className = 'fas fa-search-minus';
      text.textContent = `-${percent}%`;
      indicator.style.color = '#ef4444';
    } else if (type === 'limit') {
      icon.className = 'fas fa-ban';
      text.textContent = '已到极限';
      indicator.style.color = '#f59e0b';
    }

    indicator.style.display = 'flex';

  }

  setupKeyboardControls() {
    let that = this;
    // 空格键按下 - 进入平移模式
    document.addEventListener('keydown', function (e: any) {
      // 空格键 - 切换平移模式
      // console.log(e, e.code, !that.isSpacePressed, '11111')
      if (e.code === 'Space' && !that.isSpacePressed) {
        that.isSpacePressed = true;
        that.updateCursorForPanning(true);
        e.preventDefault(); // 防止页面滚动
      }
    });

    // 空格键释放 - 退出平移模式
    document.addEventListener('keyup', function (e: any) {
      if (e.code === 'Space' && that.isSpacePressed) {
        that.isSpacePressed = false;
        that.updateCursorForPanning(false);
      }
    });

    // 防止空格键默认行为
    document.addEventListener('keydown', function (e: any) {
      if (e.code === 'Space' && e.target === document.body) {
        e.preventDefault();
      }
    });



  }

  updateCursorForPanning(isPanning) {
    // if (isPanning) {
    //   this.canvas.defaultCursor = 'grab';
    //   this.canvas.hoverCursor = 'grab';
    //   this.canvas.selection = false;

    //   this.updateStatus('平移模式 - 拖动鼠标移动画布 (释放空格键退出)');
    // } else {
    //   if (this.currentDrawingMode === 'select') {
    //     this.canvas.defaultCursor = 'default';
    //     this.canvas.hoverCursor = 'default';
    //     this.canvas.selection = true;
    //   } else {
    //     this.canvas.defaultCursor = 'crosshair';
    //     this.canvas.hoverCursor = 'crosshair';
    //     this.canvas.selection = false;
    //   }
    //   this.updateStatus('就绪 - 使用鼠标滚轮缩放，按住空格键拖动画布');
    // }
    // this.canvas.renderAll();
  }

  // 限制平移范围，防止图片拖出画布太远
  constrainPan() {
    const zoom = this.canvas.getZoom();
    const vpt = this.canvas.viewportTransform;
    const canvasWidth = this.canvas.getWidth();
    const canvasHeight = this.canvas.getHeight();
    const contentWidth = canvasWidth * zoom;
    const contentHeight = canvasHeight * zoom;
    // 保证图片至少有 50px 可见，防止完全拖出屏幕，但不限制正常查看
    const minVisible = 50;
    vpt[4] = Math.min(canvasWidth - minVisible, Math.max(vpt[4], minVisible - contentWidth));
    vpt[5] = Math.min(canvasHeight - minVisible, Math.max(vpt[5], minVisible - contentHeight));
  }

  // 惯性滑动
  applyPanMomentum(vx: number, vy: number) {
    const friction = 0.92;
    const minSpeed = 0.5;
    const animate = () => {
      if (Math.abs(vx) < minSpeed && Math.abs(vy) < minSpeed) {
        this.panAnimationId = null;
        this.refreshAfterViewportChange();
        return;
      }
      vx *= friction;
      vy *= friction;
      const vpt = this.canvas.viewportTransform;
      vpt[4] += vx;
      vpt[5] += vy;
      this.constrainPan();
      this.canvas.requestRenderAll();
      this.panAnimationId = requestAnimationFrame(animate);
    };
    this.panAnimationId = requestAnimationFrame(animate);
  }

  private refreshAfterViewportChange() {
    if (!this.canvas) return;
    try {
      // Ensure Fabric re-calculates canvas offset (important on mobile/webview)
      this.canvas.calcOffset();
      // Update object coords for accurate hit testing after viewport transform changes
      this.canvas.forEachObject(obj => {
        if (obj && (obj as any).setCoords) {
          (obj as any).setCoords();
        }
      });
      this.canvas.requestRenderAll();
    } catch (e) {
      // ignore
    }
  }

  resetZoom() {
    this.canvas.setZoom(1);
    this.canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
    // currentZoom = 1;
    this.updateZoomDisplay();
    this.refreshAfterViewportChange();
    // updateStatus('视图已重置');
  }


  addMarkerAtPosition(xData, yData, id?, status?) {
    // 创建标记点
    // let marker: any = this.createMarker(pointer.x, pointer.y, 'test');
    let that = this;
    // 使用在线图片URL
    // const iconUrl = '../../../../assets/icon/map-icon.svg';

    let iconUrl = '';
    if (this.displayDifferentStates) {
      iconUrl = status && status == '_SYS_PL1_7' ? '../../../../assets/icon/location_fill-green.png' : '../../../../assets/icon/location_red1.png';
    } else {
      iconUrl = '../../../../assets/icon/location_red1.png';
    }
    
    fabric.Image.fromURL(iconUrl, function (img) {
      const markerIcon = new fabric.Group([
        img.set({
          scaleX: 0.1,
          scaleY: 0.1,
          originX: 'center',
          originY: 'center',
          // width: 20,
          // height: 20
        })
      ], {
        // left: xData - 1,
        // top: yData - 6,
        left: xData,
        top: yData,
        originX: 'center',
        originY: 'center',
        selectable: false,
        hasControls: false,
        hasBorders: false,
        lockScalingX: true,
        lockScalingY: true,
        lockUniScaling: true,
        hoverCursor: 'pointer',
        name: id ? ('point_' + id) : 'point',
        // isMarker: true,
        // markerId: markerId,
      });
      (markerIcon as any).isFloorPlanMarker = true;
      // 保存标记点信息
      const markerData = {
        // id: markerId,
        // name: name,
        xAxis: xData,
        yAxis: yData,
        // color: markerColor,
        object: markerIcon,
        mark: id ? ('point_' + id) : 'point',
      };

      that.canvas.add(markerIcon);
      that.canvas.renderAll();
      // console.log(markerData, 'markerData');
      // 目前只做一个点，后期再拓展
      that.markers.push(markerData);
      that.markPointEvent.emit(that.markers);
      // return markerData;
    });
  }

  //开始触摸
  startMap(e: any) {
    // const product_fuide = document.getElementsByClassName('canvasContainerBox')[0] as HTMLElement

    // if (product_fuide) {

    //   this.ol = e.touches[0].clientX - product_fuide.offsetLeft;
    //   this.ot = e.touches[0].clientY - product_fuide.offsetTop;
    //   var touches = e.touches;
    //   var events = touches[0];
    //   var events2 = touches[1];
    //   if (!events) {
    //     return;
    //   }

    //   // 第一个触摸点的坐标
    //   this.store.pageX = events.pageX;
    //   this.store.pageY = events.pageY;
    //   // this.store.moveable = true;
    //   if (events2) {
    //     this.store.pageX2 = events2.pageX;
    //     this.store.pageY2 = events2.pageY;
    //   }
    //   this.store.originScale = this.store.scale || 1;
    //   // console.log(index)
    // }
  }
  // 移动中
  moveMap(e: any) {
    // const product_fuide = document.getElementsByClassName('canvasContainerBox')[0] as HTMLElement

    // if (product_fuide) {
    //   let left = e.touches[0].clientX;
    //   let top = e.touches[0].clientY;
    //   this.top = top - this.ot - 100;
    //   this.left = left - this.ol - 20;
    //   // e.preventDefault();
    //   let store = this.store
    //   var touches = e.touches;
    //   var events = touches[0];
    //   var events2 = touches[1];
    //   if (events2) {
    //     // 双指移动
    //     if (!store.pageX2) {
    //       store.pageX2 = events2.pageX;
    //     }
    //     if (!store.pageY2) {
    //       store.pageY2 = events2.pageY;
    //     }

    //     // 获取坐标之间的举例
    //     var getDistance = function (start: any, stop: any) {
    //       return Math.hypot(stop.x - start.x, stop.y - start.y);
    //     };

    //     var zoom = getDistance({
    //       x: events.pageX,
    //       y: events.pageY
    //     }, {
    //       x: events2.pageX,
    //       y: events2.pageY
    //     }) /
    //       getDistance({
    //         x: store.pageX,
    //         y: store.pageY
    //       }, {
    //         x: store.pageX2,
    //         y: store.pageY2
    //       });

    //     var newScale = store.originScale * zoom;
    //     // 最大缩放比例限制
    //     if (newScale > 3) {
    //       newScale = 3;
    //     } else if (newScale < 1) {
    //       newScale = 1;
    //     }
    //     // 记住使用的缩放值
    //     this.store.scale = newScale;
    //   }
    // }
  }
  //结束
  endMap() {

  }
}
